import { useEffect, useCallback, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({
  images = [],
  activeIdx = 0,
  open = false,
  onClose,
  onChange,
  grayscale = false,
  alt = "",
}) {
  const total = images.length;
  const [fading, setFading] = useState(false);

  const navigate = useCallback((nextIdx) => {
    if (total <= 1) return;
    setFading(true);
    setTimeout(() => {
      onChange(nextIdx);
      setFading(false);
    }, 180);
  }, [total, onChange]);

  const goPrev = useCallback(() => {
    navigate((activeIdx - 1 + total) % total);
  }, [activeIdx, total, navigate]);

  const goNext = useCallback(() => {
    navigate((activeIdx + 1) % total);
  }, [activeIdx, total, navigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, goPrev, goNext]);

  if (!open) return null;

  return (
    <div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
      className="fixed inset-0 z-[1000] bg-black flex flex-col"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-6 sm:px-10 py-5 shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Counter */}
        <span
          data-testid="lightbox-counter"
          className="text-white/50 text-xs tracking-[0.25em] uppercase font-medium tabular-nums"
        >
          {String(activeIdx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close gallery"
          data-testid="lightbox-close"
          className="text-white/60 hover:text-white transition-colors p-1"
        >
          <X size={20} strokeWidth={1.5} />
        </button>
      </div>

      {/* Image area */}
      <div
        className="flex-1 relative flex items-center justify-center px-14 sm:px-20 pb-10 min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={images[activeIdx]}
          src={images[activeIdx]}
          alt={`${alt} — photo ${activeIdx + 1}`}
          data-testid="lightbox-image"
          className={`max-w-full max-h-full object-contain transition-opacity duration-[180ms] ${
            fading ? "opacity-0" : "opacity-100"
          } ${grayscale ? "grayscale" : ""}`}
        />

        {/* Prev */}
        {total > 1 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            aria-label="Previous photo"
            data-testid="lightbox-prev"
            className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors p-3"
          >
            <ChevronLeft size={28} strokeWidth={1} />
          </button>
        )}

        {/* Next */}
        {total > 1 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            aria-label="Next photo"
            data-testid="lightbox-next"
            className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors p-3"
          >
            <ChevronRight size={28} strokeWidth={1} />
          </button>
        )}
      </div>

      {/* Dot indicators (minimal, at bottom) */}
      {total > 1 && total <= 10 && (
        <div
          className="flex justify-center gap-1.5 pb-6 shrink-0"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-dots"
        >
          {images.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => navigate(idx)}
              aria-label={`Go to photo ${idx + 1}`}
              className={`w-1 h-1 rounded-full transition-all duration-300 ${
                idx === activeIdx ? "bg-white w-4" : "bg-white/30"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
